import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AgeGroupSelect from "../../components/AgeGroupSelect";
import SportTypeSelect from "../../components/SportTypeSelect";
import FilterSelect from "../../components/FilterSelect";
import RichTextEditor from "../../components/RichTextEditor";
import QuestionEditorModal from "./QuestionEditorModal";
import { cleanQuestionAnswers, normalizeTrueFalseIndex } from "../../lib/quizQuestionAnswers";
import { usePageTitle } from "../../hooks/usePageTitle";
import {
  getQuizzes,
  getQuizById,
  createQuiz,
  updateQuiz,
  deleteQuiz,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  getQuizAssignments,
  createQuizAssignment,
  deleteQuizAssignment,
  getQuizStats,
  getQuizAnalytics,
  getQuizAdminStats,
  type QuizAnalytics,
  type QuizAdminStats,
  type Quiz,
  type QuizStats,
  type QuizQuestion,
  type QuizWithQuestions,
  type QuizAssignment,
  type QuizVideoMode,
  type VideoChapter,
} from "../../services/quizzes";
import { getSports, type PlatformSport } from "../../services/sports";
import { getQuizCategories, type QuizCategory } from "../../services/quiz-categories";
import { getQuizThemes, type QuizTheme } from "../../services/quiz-themes";
import { getAgeGroups, type AgeGroup } from "../../services/api";
import { formatDate } from "../../lib/dateFormat";
import DateInput from "../../components/DateInput";

/**
 * Benzile de vârstă, ca în backend (lib/quizAudience.ts).
 *
 * Grupele configurate sunt 17, câte una pe an; un capitol nu se scrie de 17
 * ori, se scrie o dată pe bandă. "" înseamnă „toate vârstele" — starea
 * întregului conținut existent, care rămâne vizibil tuturor.
 */
const AGE_BANDS: Record<"mic" | "mediu" | "mare", { age_min: number; age_max: number; label: string }> = {
  mic: { age_min: 5, age_max: 8, label: "Mic (5-8 ani)" },
  mediu: { age_min: 9, age_max: 12, label: "Mediu (9-12 ani)" },
  mare: { age_min: 13, age_max: 19, label: "Mare (13-19 ani)" },
};

function boundsForBand(band: "" | "mic" | "mediu" | "mare"): { age_min: number | null; age_max: number | null } {
  if (band && AGE_BANDS[band]) return { age_min: AGE_BANDS[band].age_min, age_max: AGE_BANDS[band].age_max };
  return { age_min: null, age_max: null };
}

function bandFromBounds(min: number | null | undefined, max: number | null | undefined): "" | "mic" | "mediu" | "mare" {
  for (const [name, b] of Object.entries(AGE_BANDS)) {
    if (min === b.age_min && max === b.age_max) return name as "mic" | "mediu" | "mare";
  }
  return "";
}



export default function QuizManagement() {
  const { t } = useTranslation();
  usePageTitle(t("admin.quizManagement.title", "Gestionare quiz-uri"));

  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [selectedQuiz, setSelectedQuiz] = useState<QuizWithQuestions | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sportTypeFilter, setSportTypeFilter] = useState("");
  const [ageGroupFilter, setAgeGroupFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [themeFilter, setThemeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft" | "noq">("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [showCatPanel, setShowCatPanel] = useState(false);
  const [adminStats, setAdminStats] = useState<QuizAdminStats | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Modals
  const [showCreateQuizModal, setShowCreateQuizModal] = useState(false);
  const [showEditQuizModal, setShowEditQuizModal] = useState(false);
  const [showDeleteQuizModal, setShowDeleteQuizModal] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<QuizQuestion | null>(null);
  const [assignments, setAssignments] = useState<QuizAssignment[]>([]);

  // Per-quiz stats modal — populated lazily when the admin clicks the
  // Stats button on a row so we don't fan-out an extra request per row
  // on the list page.
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState<QuizStats | null>(null);
  const [statsForQuiz, setStatsForQuiz] = useState<Quiz | null>(null);

  const openStatsModal = async (quiz: Quiz) => {
    setStatsForQuiz(quiz);
    setShowStatsModal(true);
    setStatsLoading(true);
    setStatsData(null);
    try {
      const data = await getQuizStats(quiz.id);
      setStatsData(data);
    } catch (e: any) {
      console.error("Failed to load quiz stats:", e);
      alert(e?.response?.data?.error || "Nu s-au putut încărca statisticile quiz-ului");
    } finally {
      setStatsLoading(false);
    }
  };

  // Per-question analytics
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<QuizAnalytics | null>(null);
  const [analyticsForQuiz, setAnalyticsForQuiz] = useState<Quiz | null>(null);

  const openAnalyticsModal = async (quiz: Quiz) => {
    setAnalyticsForQuiz(quiz);
    setShowAnalyticsModal(true);
    setAnalyticsLoading(true);
    setAnalyticsData(null);
    try {
      setAnalyticsData(await getQuizAnalytics(quiz.id));
    } catch (e: any) {
      console.error("Failed to load analytics:", e);
      alert(e?.response?.data?.error || "Nu s-au putut încărca analizele");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Platform sports for multi-select
  const [platformSports, setPlatformSports] = useState<PlatformSport[]>([]);
  const [showSportDropdown, setShowSportDropdown] = useState(false);

  // Quiz categories
  const [quizCategories, setQuizCategories] = useState<QuizCategory[]>([]);
  const [quizThemes, setQuizThemes] = useState<QuizTheme[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);

  // Video source type
  type VideoSourceType = "self_hosted" | "youtube" | "vimeo";
  const [videoSourceType, setVideoSourceType] = useState<VideoSourceType>("youtube");
  const [videoDurationMinutes, setVideoDurationMinutes] = useState(0);
  const [videoDurationSeconds, setVideoDurationSeconds] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form states
  const [quizForm, setQuizForm] = useState({
    title: "",
    description: "",
    content: "",
    topic: "",
    difficulty: "easy" as "easy" | "medium" | "hard",
    category: "",
    theme_id: "",
    // Position within the theme for sequential progression (1, 2, 3 …).
    // Empty → unordered (sorts last, never gates).
    theme_order: "" as number | "",
    // Whether this quiz is playable by Free-plan athletes.
    free_included: false,
    sport_type: "",
    age_group: [] as string[],
    // Banda de vârstă pentru care e scris capitolul. "" = toate vârstele,
    // care e starea întregului conținut existent. Se traduce în age_min /
    // age_max la salvare; navigarea sportivului filtrează după ele.
    age_band: "" as "" | "mic" | "mediu" | "mare",
    reward_points: 0,
    is_active: false, // new quizzes start as draft (hidden) until published
    // F1: speed scoring. `time_limit_seconds` = per-question answer
    // timer. `preview_seconds` = reading window before options appear.
    // Empty string → treated as null server-side (no timer, auto
    // preview from word count).
    //
    // Un quiz nou pornește cu 10 secunde, nu gol. Fără cronometru nu există
    // nici numărătoare inversă, nici bonus de viteză, iar copilul vede
    // „până la +100 puncte" și primește doar baza.
    time_limit_seconds: 10 as number | "",
    preview_seconds: "" as number | "",
    // Video fields
    video_mode: "none" as QuizVideoMode,
    video_url: "",
    video_duration: 0,
    video_chapters: [] as VideoChapter[],
  });

  // Update video duration when minutes or seconds change
  const updateVideoDuration = useCallback((mins: number, secs: number) => {
    const totalSeconds = (mins * 60) + secs;
    setQuizForm(prev => ({ ...prev, video_duration: totalSeconds }));
  }, []);

  // Handle file drop for self-hosted video
  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('video/')) {
      // In production, this would upload to a storage service
      // For now, we'll use a placeholder URL
      const fileName = file.name;
      setQuizForm(prev => ({ ...prev, video_url: `/uploads/videos/${fileName}` }));
      alert(`Videoul "${fileName}" a fost selectat. În producție, acesta ar fi încărcat în stocarea cloud.`);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      const fileName = file.name;
      setQuizForm(prev => ({ ...prev, video_url: `/uploads/videos/${fileName}` }));
      alert(`Videoul "${fileName}" a fost selectat. În producție, acesta ar fi încărcat în stocarea cloud.`);
    }
  }, []);

  // Video preview and duration fetching
  const [videoPreviewId, setVideoPreviewId] = useState<string | null>(null);
  const [fetchingDuration, setFetchingDuration] = useState(false);

  // Helper function to extract YouTube video ID from various URL formats
  const extractYouTubeId = useCallback((url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }, []);

  // Helper function to extract Vimeo video ID
  const extractVimeoId = useCallback((url: string): string | null => {
    const patterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/,
      /^(\d+)$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }, []);

  // Fetch video duration from oEmbed API
  const fetchVideoDuration = useCallback(async (url: string, source: VideoSourceType) => {
    if (!url || source === "self_hosted") return;

    setFetchingDuration(true);
    try {
      if (source === "youtube") {
        const videoId = extractYouTubeId(url);
        if (videoId) {
          setVideoPreviewId(videoId);
          // YouTube oEmbed doesn't return duration, but we show preview
          // Duration must be entered manually or use YouTube Data API (requires API key)
        }
      } else if (source === "vimeo") {
        const videoId = extractVimeoId(url);
        if (videoId) {
          setVideoPreviewId(videoId);
          // Fetch duration from Vimeo oEmbed API
          const response = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.duration) {
              const totalSeconds = Math.round(data.duration);
              const mins = Math.floor(totalSeconds / 60);
              const secs = totalSeconds % 60;
              setVideoDurationMinutes(mins);
              setVideoDurationSeconds(secs);
              setQuizForm(prev => ({ ...prev, video_duration: totalSeconds }));
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching video duration:", error);
    } finally {
      setFetchingDuration(false);
    }
  }, [extractYouTubeId, extractVimeoId]);

  // Update video preview when URL changes
  useEffect(() => {
    const url = quizForm.video_url;
    if (!url) {
      setVideoPreviewId(null);
      return;
    }

    // Debounce the fetch to avoid too many requests while typing
    const timeoutId = setTimeout(() => {
      if (videoSourceType === "youtube") {
        const id = extractYouTubeId(url);
        setVideoPreviewId(id);
      } else if (videoSourceType === "vimeo") {
        const id = extractVimeoId(url);
        setVideoPreviewId(id);
        if (id) {
          fetchVideoDuration(url, videoSourceType);
        }
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [quizForm.video_url, videoSourceType, extractYouTubeId, extractVimeoId, fetchVideoDuration]);

  // Image drag-drop for questions
  const [isImageDragging, setIsImageDragging] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleImageDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsImageDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      // In production, this would upload to a storage service
      const fileName = file.name;
      setQuestionForm(prev => ({ ...prev, media_url: `/uploads/images/${fileName}` }));
      alert(`Imaginea "${fileName}" a fost selectată. În producție, aceasta ar fi încărcată în stocarea cloud.`);
    }
  }, []);

  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const fileName = file.name;
      setQuestionForm(prev => ({ ...prev, media_url: `/uploads/images/${fileName}` }));
      alert(`Imaginea "${fileName}" a fost selectată. În producție, aceasta ar fi încărcată în stocarea cloud.`);
    }
  }, []);

  const [questionForm, setQuestionForm] = useState({
    text: "",
    media_type: "text" as "text" | "image" | "video",
    media_url: "",
    video_duration: 0,
    kind: "single" as "single" | "multi" | "true_false",
    options: ["", "", "", ""],
    correct_answer: 0 as number | number[], // number for single/true_false, number[] for multi
    explanation: "",
    // Points fields
    points: 10,
    // Per-question answer timer in seconds (empty = fall back to quiz timer).
    time_limit_seconds: "" as number | "",
    chapter_index: null as number | null,
  });

  const [assignmentForm, setAssignmentForm] = useState({
    age_group: [] as string[],
    quizzes_per_week: 1,
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    is_active: true,
  });

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Any filter change resets to page 1.
  useEffect(() => {
    setPage(1);
  }, [categoryFilter, sportTypeFilter, ageGroupFilter, difficultyFilter, themeFilter, statusFilter, searchQuery, pageSize]);

  useEffect(() => {
    fetchQuizzes();
  }, [page, pageSize, categoryFilter, sportTypeFilter, ageGroupFilter, difficultyFilter, themeFilter, statusFilter, searchQuery]);

  // Counters (per-type views + per-category) follow the non-view filters.
  useEffect(() => {
    fetchAdminStats();
  }, [categoryFilter, sportTypeFilter, ageGroupFilter, difficultyFilter, searchQuery]);

  // Fetch platform sports and quiz categories on mount
  useEffect(() => {
    const loadSports = async () => {
      try {
        const sports = await getSports();
        setPlatformSports(sports);
      } catch (error) {
        console.error("Failed to fetch platform sports:", error);
      }
    };
    const loadCategories = async () => {
      try {
        const categories = await getQuizCategories();
        setQuizCategories(categories);
      } catch (error) {
        console.error("Failed to fetch quiz categories:", error);
      }
    };
    const loadThemes = async () => {
      try {
        const themes = await getQuizThemes();
        setQuizThemes(themes);
      } catch (error) {
        console.error("Failed to fetch quiz themes:", error);
      }
    };
    const loadAgeGroups = async () => {
      try {
        const groups = await getAgeGroups({ all: true });
        setAgeGroups(groups);
      } catch (error) {
        console.error("Failed to fetch age groups:", error);
      }
    };
    loadSports();
    loadCategories();
    loadThemes();
    loadAgeGroups();
  }, []);

  // Normalize an age-group name for loose matching between the fixed
  // U1..U25 picker values and admin-defined group names (e.g. "U-12").
  const normalizeAgeName = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, "");

  // Given the age groups selected in the quiz form, return the single
  // recommended difficulty if all matched groups agree on one; otherwise null.
  const recommendedDifficultyFor = useCallback(
    (selected: string[]): "easy" | "medium" | "hard" | null => {
      if (!selected.length || !ageGroups.length) return null;
      const diffs = new Set<string>();
      for (const sel of selected) {
        const key = normalizeAgeName(sel);
        const match = ageGroups.find(
          (g) => g.default_difficulty && normalizeAgeName(g.name) === key
        );
        if (match?.default_difficulty) diffs.add(match.default_difficulty);
      }
      if (diffs.size === 1) return Array.from(diffs)[0] as "easy" | "medium" | "hard";
      return null;
    },
    [ageGroups]
  );

  // When the age-group selection changes in the quiz form, auto-apply the
  // recommended difficulty from the matching admin-defined group(s).
  const handleQuizAgeGroupChange = (vals: string[]) => {
    const rec = recommendedDifficultyFor(vals);
    setQuizForm((prev) => ({
      ...prev,
      age_group: vals,
      difficulty: rec ?? prev.difficulty,
    }));
  };

  // Helper functions for sport type multi-select
  const getSelectedSportTypes = () => {
    return quizForm.sport_type ? quizForm.sport_type.split(",").map(s => s.trim()).filter(Boolean) : [];
  };

  const toggleSportType = (sportName: string) => {
    const selected = getSelectedSportTypes();
    if (selected.includes(sportName)) {
      const newSelected = selected.filter(s => s !== sportName);
      setQuizForm({ ...quizForm, sport_type: newSelected.join(", ") });
    } else {
      setQuizForm({ ...quizForm, sport_type: [...selected, sportName].join(", ") });
    }
  };

  const fetchQuizzes = async () => {
    setLoading(true);
    try {
      const params: Parameters<typeof getQuizzes>[0] = {
        category: categoryFilter || undefined,
        sport_type: sportTypeFilter || undefined,
        age_group: ageGroupFilter || undefined,
        difficulty: difficultyFilter || undefined,
        themeId: themeFilter || undefined,
        search: searchQuery || undefined,
        page,
        pageSize,
      };
      if (statusFilter === "published") params.is_active = true;
      else if (statusFilter === "draft") params.is_active = false;
      else if (statusFilter === "noq") params.has_questions = false;
      const data = await getQuizzes(params);
      setQuizzes(data.data);
      setTotal(data.total);
    } catch (error) {
      console.error("Failed to fetch quizzes:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminStats = async () => {
    try {
      const data = await getQuizAdminStats({
        category: categoryFilter || undefined,
        sport_type: sportTypeFilter || undefined,
        age_group: ageGroupFilter || undefined,
        difficulty: difficultyFilter || undefined,
        search: searchQuery || undefined,
      });
      setAdminStats(data);
    } catch (error) {
      console.error("Failed to fetch quiz stats:", error);
    }
  };

  // ── Bulk selection helpers ────────────────────────────────────────
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const allOnPage = quizzes.map((q) => q.id);
      const everySelected = allOnPage.length > 0 && allOnPage.every((id) => prev.has(id));
      return everySelected ? new Set() : new Set(allOnPage);
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const afterBulk = async () => {
    clearSelection();
    await Promise.all([fetchQuizzes(), fetchAdminStats()]);
  };
  const bulkSetActive = async (active: boolean) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      await Promise.all(Array.from(selectedIds).map((id) => updateQuiz(id, { is_active: active } as any)));
      await afterBulk();
    } catch (e) {
      console.error("Bulk status update failed:", e);
      alert(t("quiz.bulk_failed", "Acțiunea în masă a eșuat"));
    } finally {
      setBulkBusy(false);
    }
  };
  const bulkSetCategory = async (category: string) => {
    if (selectedIds.size === 0 || !category) return;
    setBulkBusy(true);
    try {
      await Promise.all(Array.from(selectedIds).map((id) => updateQuiz(id, { category } as any)));
      await afterBulk();
    } catch (e) {
      console.error("Bulk category update failed:", e);
      alert(t("quiz.bulk_failed", "Acțiunea în masă a eșuat"));
    } finally {
      setBulkBusy(false);
    }
  };
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(t("quiz.bulk_delete_confirm", "Ștergi cele {{n}} quiz-uri selectate?").replace("{{n}}", String(selectedIds.size)))) return;
    setBulkBusy(true);
    try {
      await Promise.all(Array.from(selectedIds).map((id) => deleteQuiz(id)));
      await afterBulk();
    } catch (e) {
      console.error("Bulk delete failed:", e);
      alert(t("quiz.bulk_failed", "Acțiunea în masă a eșuat"));
    } finally {
      setBulkBusy(false);
    }
  };

  const fetchQuizWithQuestions = async (quizId: string) => {
    try {
      const data = await getQuizById(quizId);
      setSelectedQuiz(data);
    } catch (error) {
      console.error("Failed to fetch quiz:", error);
    }
  };

  const fetchAssignments = async (quizId: string) => {
    try {
      const data = await getQuizAssignments(quizId);
      setAssignments(data);
    } catch (error) {
      console.error("Failed to fetch assignments:", error);
    }
  };

  const openAssignmentsModal = async (quiz: Quiz) => {
    setSelectedQuiz(quiz as QuizWithQuestions);
    await fetchAssignments(quiz.id);
    setShowAssignmentsModal(true);
  };

  const handleCreateAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQuiz) return;
    try {
      await createQuizAssignment(selectedQuiz.id, { ...assignmentForm, age_group: assignmentForm.age_group.length ? assignmentForm.age_group.join(", ") : "" });
      resetAssignmentForm();
      await fetchAssignments(selectedQuiz.id);
    } catch (error) {
      console.error("Failed to create assignment:", error);
      alert("Crearea programării a eșuat");
    }
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (!selectedQuiz) return;
    if (!window.confirm("Sigur vrei să ștergi această programare?")) return;
    try {
      await deleteQuizAssignment(selectedQuiz.id, assignmentId);
      await fetchAssignments(selectedQuiz.id);
    } catch (error) {
      console.error("Failed to delete assignment:", error);
      alert("Ștergerea programării a eșuat");
    }
  };

  const resetAssignmentForm = () => {
    setAssignmentForm({
      age_group: [],
      quizzes_per_week: 1,
      start_date: new Date().toISOString().split("T")[0],
      end_date: "",
      is_active: true,
    });
  };

  const handleCreateQuiz = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createQuiz({
        title: quizForm.title,
        description: quizForm.description || undefined,
        content: quizForm.content || undefined,
        topic: quizForm.topic,
        difficulty: quizForm.difficulty,
        category: quizForm.category || undefined,
        theme_id: quizForm.theme_id || undefined,
        theme_order: quizForm.theme_order === "" ? null : Number(quizForm.theme_order),
        free_included: quizForm.free_included,
        sport_type: quizForm.sport_type || null,
        // Send null (not undefined) when cleared so the backend actually removes
        // the age groups — undefined means "leave unchanged".
        age_group: quizForm.age_group.length ? quizForm.age_group.join(", ") : null,
        ...boundsForBand(quizForm.age_band),
        reward_points: quizForm.reward_points,
        is_active: quizForm.is_active,
        time_limit_seconds: quizForm.time_limit_seconds === "" ? null : Number(quizForm.time_limit_seconds),
        preview_seconds: quizForm.preview_seconds === "" ? null : Number(quizForm.preview_seconds),
        video_mode: quizForm.video_mode,
        video_url: quizForm.video_url || undefined,
        video_duration: quizForm.video_duration || undefined,
        video_chapters: quizForm.video_chapters.length > 0 ? quizForm.video_chapters : undefined,
      } as any);
      setShowCreateQuizModal(false);
      resetQuizForm();
      fetchQuizzes();
    } catch (error) {
      console.error("Failed to create quiz:", error);
      alert("Crearea quiz-ului a eșuat");
    }
  };

  const handleUpdateQuiz = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQuiz) return;
    try {
      await updateQuiz(selectedQuiz.id, {
        title: quizForm.title,
        description: quizForm.description || undefined,
        content: quizForm.content || undefined,
        topic: quizForm.topic,
        difficulty: quizForm.difficulty,
        category: quizForm.category || undefined,
        theme_id: quizForm.theme_id || null,
        theme_order: quizForm.theme_order === "" ? null : Number(quizForm.theme_order),
        free_included: quizForm.free_included,
        sport_type: quizForm.sport_type || null,
        // Send null (not undefined) when cleared so the backend actually removes
        // the age groups — undefined means "leave unchanged".
        age_group: quizForm.age_group.length ? quizForm.age_group.join(", ") : null,
        ...boundsForBand(quizForm.age_band),
        reward_points: quizForm.reward_points,
        is_active: quizForm.is_active,
        time_limit_seconds: quizForm.time_limit_seconds === "" ? null : Number(quizForm.time_limit_seconds),
        preview_seconds: quizForm.preview_seconds === "" ? null : Number(quizForm.preview_seconds),
        video_mode: quizForm.video_mode,
        video_url: quizForm.video_url || null,
        video_duration: quizForm.video_duration || null,
        video_chapters: quizForm.video_chapters.length > 0 ? quizForm.video_chapters : null,
      } as any);
      setShowEditQuizModal(false);
      setSelectedQuiz(null);
      resetQuizForm();
      fetchQuizzes();
    } catch (error) {
      console.error("Failed to update quiz:", error);
      alert("Actualizarea quiz-ului a eșuat");
    }
  };

  const handleDeleteQuiz = async () => {
    if (!selectedQuiz) return;
    try {
      await deleteQuiz(selectedQuiz.id);
      setShowDeleteQuizModal(false);
      setSelectedQuiz(null);
      fetchQuizzes();
    } catch (error) {
      console.error("Failed to delete quiz:", error);
      alert("Ștergerea quiz-ului a eșuat");
    }
  };

  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQuiz) return;
    const cleaned = cleanQuestionAnswers(questionForm.kind, questionForm.options, questionForm.correct_answer);
    if (!cleaned.ok) {
      alert(cleaned.error);
      return;
    }
    try {
      await addQuestion(selectedQuiz.id, {
        text: questionForm.text,
        media_type: questionForm.media_type,
        media_url: questionForm.media_url || undefined,
        video_duration: questionForm.video_duration || undefined,
        kind: questionForm.kind,
        options: cleaned.options,
        correct_answer: cleaned.correct_answer,
        explanation: questionForm.explanation.trim() || null,
        points: questionForm.points,
        time_limit_seconds: questionForm.time_limit_seconds === "" ? null : Number(questionForm.time_limit_seconds),
        chapter_index: questionForm.chapter_index !== null ? questionForm.chapter_index : undefined,
      });
      setShowAddQuestionModal(false);
      resetQuestionForm();
      fetchQuizWithQuestions(selectedQuiz.id);
    } catch (error: any) {
      console.error("Failed to add question:", error);
      alert(error.response?.data?.error || "Adăugarea întrebării a eșuat");
    }
  };

  const handleUpdateQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQuiz || !selectedQuestion) return;
    const cleaned = cleanQuestionAnswers(questionForm.kind, questionForm.options, questionForm.correct_answer);
    if (!cleaned.ok) {
      alert(cleaned.error);
      return;
    }
    try {
      await updateQuestion(selectedQuiz.id, selectedQuestion.id, {
        text: questionForm.text,
        media_type: questionForm.media_type,
        media_url: questionForm.media_url || undefined,
        video_duration: questionForm.video_duration || undefined,
        kind: questionForm.kind,
        options: cleaned.options,
        correct_answer: cleaned.correct_answer,
        explanation: questionForm.explanation.trim() || null,
        points: questionForm.points,
        time_limit_seconds: questionForm.time_limit_seconds === "" ? null : Number(questionForm.time_limit_seconds),
        chapter_index: questionForm.chapter_index,
      });
      setShowEditQuestionModal(false);
      setSelectedQuestion(null);
      resetQuestionForm();
      fetchQuizWithQuestions(selectedQuiz.id);
    } catch (error: any) {
      console.error("Failed to update question:", error);
      alert(error.response?.data?.error || "Actualizarea întrebării a eșuat");
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!selectedQuiz) return;
    if (!window.confirm("Ștergi această întrebare?")) return;
    try {
      await deleteQuestion(selectedQuiz.id, questionId);
      fetchQuizWithQuestions(selectedQuiz.id);
    } catch (error) {
      console.error("Failed to delete question:", error);
      alert("Ștergerea întrebării a eșuat");
    }
  };

  const resetQuizForm = () => {
    setQuizForm({
      title: "",
      description: "",
      content: "",
      topic: "",
      difficulty: "easy",
      category: "",
      theme_id: "",
      theme_order: "",
      free_included: false,
      sport_type: "",
      age_group: [],
      age_band: "",
      reward_points: 0,
      is_active: false, // new quizzes start as draft (hidden) until published
      time_limit_seconds: 10,
      preview_seconds: "",
      video_mode: "none",
      video_url: "",
      video_duration: 0,
      video_chapters: [],
    });
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      text: "",
      media_type: "text",
      media_url: "",
      video_duration: 0,
      kind: "single",
      options: ["", "", "", ""],
      correct_answer: 0,
      explanation: "",
      points: 10,
      time_limit_seconds: "",
      chapter_index: null,
    });
  };

  const openEditQuizModal = (quiz: Quiz) => {
    setSelectedQuiz(quiz as QuizWithQuestions);
    // Calculate minutes and seconds from video_duration
    const duration = quiz.video_duration || 0;
    setVideoDurationMinutes(Math.floor(duration / 60));
    setVideoDurationSeconds(duration % 60);
    setQuizForm({
      title: quiz.title,
      description: quiz.description || "",
      content: quiz.content || "",
      topic: quiz.topic,
      difficulty: quiz.difficulty,
      category: quiz.category,
      theme_id: (quiz as any).theme_id || "",
      theme_order: (quiz as any).theme_order ?? "",
      free_included: (quiz as any).free_included ?? false,
      sport_type: quiz.sport_type || "",
      age_group: quiz.age_group ? quiz.age_group.split(", ").map(s => s.trim()).filter(Boolean) : [],
      age_band: bandFromBounds((quiz as any).age_min, (quiz as any).age_max),
      reward_points: quiz.reward_points,
      is_active: quiz.is_active,
      time_limit_seconds: (quiz as any).time_limit_seconds ?? "",
      preview_seconds: (quiz as any).preview_seconds ?? "",
      video_mode: quiz.video_mode || "none",
      video_url: quiz.video_url || "",
      video_duration: quiz.video_duration || 0,
      video_chapters: quiz.video_chapters || [],
    });
    setShowEditQuizModal(true);
  };

  const openQuestionsModal = async (quiz: Quiz) => {
    await fetchQuizWithQuestions(quiz.id);
    setShowQuestionsModal(true);
  };

  const openEditQuestionModal = (question: QuizQuestion) => {
    setSelectedQuestion(question);
    // Handle correct_answer based on kind
    let correctAnswer: number | number[] = question.correct_answer || 0;
    if (question.kind === "multi") {
      correctAnswer = Array.isArray(question.correct_answer) ? question.correct_answer : [];
    } else if (question.kind === "true_false") {
      // Stored as 0/1, boolean or "true"/"false"; the editor needs 0 or 1.
      correctAnswer = normalizeTrueFalseIndex(question.correct_answer);
    }
    setQuestionForm({
      text: question.text,
      media_type: question.media_type || "text",
      media_url: question.media_url || "",
      video_duration: question.video_duration || 0,
      kind: question.kind,
      options: Array.isArray(question.options) ? question.options : ["", "", "", ""],
      correct_answer: correctAnswer,
      explanation: (question as any).explanation || "",
      points: question.points || 1,
      time_limit_seconds: (question as any).time_limit_seconds ?? "",
      chapter_index: question.chapter_index,
    });
    setShowEditQuestionModal(true);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const DIFF_META: Record<string, { label: string; cls: string }> = {
    easy: { label: t("quiz.easy", "Ușor"), cls: "bg-emerald-100 text-emerald-700" },
    medium: { label: t("quiz.medium", "Mediu"), cls: "bg-amber-100 text-amber-700" },
    hard: { label: t("quiz.hard", "Greu"), cls: "bg-rose-100 text-rose-700" },
  };
  const categoryTitle = (name: string | null) => {
    if (!name) return "—";
    const c = quizCategories.find((x) => x.name === name);
    return c ? c.title : name;
  };
  // Deterministic on-brand colour per category, plus the admin-uploaded
  // image when present — used for the row thumbnail (mockup look).
  const CAT_PALETTE = ["#4f46e5", "#059669", "#0284c7", "#7c3aed", "#d97706", "#db2777", "#0891b2", "#65a30d", "#e11d48", "#4338ca"];
  const categoryVisual = (name: string | null) => {
    const c = name ? quizCategories.find((x) => x.name === name) : undefined;
    let hash = 0;
    const key = name || "?";
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) & 0x7fffffff;
    const color = CAT_PALETTE[hash % CAT_PALETTE.length];
    return { image: c?.image_url || null, color, letter: (c?.title || name || "?").charAt(0).toUpperCase() };
  };
  const anyFilter = !!(categoryFilter || sportTypeFilter || ageGroupFilter || difficultyFilter || themeFilter || searchQuery || statusFilter !== "all");
  const clearAllFilters = () => {
    setCategoryFilter(""); setSportTypeFilter(""); setAgeGroupFilter("");
    setDifficultyFilter(""); setThemeFilter(""); setStatusFilter("all"); setSearchInput(""); setSearchQuery("");
  };
  const allOnPageSelected = quizzes.length > 0 && quizzes.every((q) => selectedIds.has(q.id));

  // Compact numbered pager window: 1 … (p-1) p (p+1) … last
  const pageWindow = (): (number | "…")[] => {
    const out: (number | "…")[] = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) out.push(i);
      else if (out[out.length - 1] !== "…") out.push("…");
    }
    return out;
  };

  const VIEW_PILLS: { key: "all" | "published" | "draft" | "noq"; label: string; count?: number; warn?: boolean }[] = [
    { key: "all", label: t("quiz.view_all", "Toate"), count: adminStats?.views.all },
    { key: "published", label: t("quiz.view_published", "Publicate"), count: adminStats?.views.active },
    { key: "draft", label: t("quiz.view_draft", "Ciornă"), count: adminStats?.views.inactive },
    { key: "noq", label: t("quiz.view_noq", "Fără întrebări"), count: adminStats?.views.no_questions, warn: true },
  ];

  // WordPress-style row action links, revealed on row hover / focus.
  const rowActions = (quiz: Quiz) => (
    <div className="flex items-center gap-1.5 mt-1 text-xs font-medium opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
      <button onClick={() => openQuestionsModal(quiz)} className="text-sky-600 hover:text-sky-800 hover:underline">
        {t("quiz.action_questions", "Întrebări")}
      </button>
      <span className="text-slate-300">|</span>
      <button onClick={() => openAnalyticsModal(quiz)} className="text-violet-600 hover:text-violet-800 hover:underline" title={t("quiz.action_analytics_hint", "Analiză (include statisticile)")}>
        {t("quiz.action_analytics", "Analiză")}
      </button>
      <span className="text-slate-300">|</span>
      <button onClick={() => openEditQuizModal(quiz)} className="text-indigo-600 hover:text-indigo-800 hover:underline">
        {t("quiz.action_edit", "Editează")}
      </button>
      <span className="text-slate-300">|</span>
      <button
        onClick={() => { setSelectedQuiz(quiz as QuizWithQuestions); setShowDeleteQuizModal(true); }}
        className="text-rose-600 hover:text-rose-800 hover:underline"
      >
        {t("quiz.action_delete", "Șterge")}
      </button>
    </div>
  );

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-5">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Conținut și educație</p>
        <h1 className="text-2xl font-bold text-slate-800">
          {t("quiz.page_title", "Quiz-uri")}
        </h1>
        <p className="text-slate-500 text-sm mt-0.5">
          <span className="tabular-nums font-semibold text-slate-700">{total.toLocaleString("ro-RO")}</span>{" "}
          {t("quiz.results", "rezultate")}
        </p>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-4 space-y-3">
        {/* search + create */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
            </svg>
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("quiz.search_ph", "Caută titlu sau temă…")}
              className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
          <div className="flex-1" />
          <button
            onClick={() => setShowCreateQuizModal(true)}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            {t("quiz.new", "Quiz nou")}
          </button>
        </div>

        {/* per-type view pills (counters) */}
        <div className="flex flex-wrap items-center gap-2">
          {VIEW_PILLS.map((p) => {
            const active = statusFilter === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setStatusFilter(p.key)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? p.warn ? "bg-rose-600 border-rose-600 text-white" : "bg-slate-800 border-slate-800 text-white"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
              >
                {p.label}
                {p.count !== undefined && (
                  <span className={`text-xs px-1.5 rounded-full tabular-nums ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>
                    {p.count.toLocaleString("ro-RO")}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* filters — all mockup-style dropdown chips */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            label={t("quiz.filter_difficulty", "Dificultate")}
            value={difficultyFilter}
            onChange={setDifficultyFilter}
            options={[
              { value: "easy", label: t("quiz.easy", "Ușor"), color: "#059669" },
              { value: "medium", label: t("quiz.medium", "Mediu"), color: "#d97706" },
              { value: "hard", label: t("quiz.hard", "Greu"), color: "#e11d48" },
            ]}
          />
          <FilterSelect
            label={t("quiz.filter_sport", "Sport")}
            value={sportTypeFilter}
            onChange={setSportTypeFilter}
            options={platformSports.map((s) => ({ value: s.name, label: s.name }))}
            searchable
          />
          <FilterSelect
            label={t("quiz.filter_agegroup", "Grupă")}
            value={ageGroupFilter}
            onChange={setAgeGroupFilter}
            options={ageGroups.map((g) => ({ value: g.name, label: g.name }))}
            searchable
          />
          <FilterSelect
            label={t("quiz.filter_theme", "Temă")}
            value={themeFilter}
            onChange={setThemeFilter}
            options={quizThemes.map((th) => ({ value: th.id, label: th.title }))}
            searchable
          />
          <button
            onClick={() => setShowCatPanel((v) => !v)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
              categoryFilter || showCatPanel ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10M4 18h10" /></svg>
            {categoryFilter ? categoryTitle(categoryFilter) : t("quiz.categories_counter", "Categorii cu contor")}
          </button>
          {anyFilter && (
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-dashed border-slate-300 text-slate-500 hover:text-rose-600 hover:border-rose-400 text-sm font-medium transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              {t("quiz.clear_filters", "Resetează filtrele")}
            </button>
          )}
        </div>

        {/* category counters panel (conditional) */}
        {showCatPanel && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 pt-1">
            {quizCategories.map((cat) => {
              const count = adminStats?.by_category[cat.name] ?? 0;
              const active = categoryFilter === cat.name;
              return (
                <button
                  key={cat.id}
                  onClick={() => setCategoryFilter(active ? "" : cat.name)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                    active ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                >
                  <span className="truncate">{cat.title}</span>
                  <span className="ml-auto text-xs tabular-nums text-slate-400">{count.toLocaleString("ro-RO")}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary stat strip */}
      {adminStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="text-2xl font-extrabold text-slate-800 tabular-nums">{adminStats.views.all.toLocaleString("ro-RO")}</div>
            <div className="text-xs text-slate-500 mt-0.5">{t("quiz.stat_total", "Quiz-uri total")}</div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="text-2xl font-extrabold text-slate-800 tabular-nums">{adminStats.views.active.toLocaleString("ro-RO")}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {t("quiz.stat_published", "Publicate")}
              {adminStats.views.all > 0 && <span className="ml-1.5 font-semibold text-emerald-600">{Math.round((adminStats.views.active / adminStats.views.all) * 100)}%</span>}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="text-2xl font-extrabold text-slate-800 tabular-nums">{adminStats.views.inactive.toLocaleString("ro-RO")}</div>
            <div className="text-xs text-slate-500 mt-0.5">{t("quiz.stat_draft", "În ciornă")}</div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className={`text-2xl font-extrabold tabular-nums ${adminStats.views.no_questions > 0 ? "text-rose-600" : "text-slate-800"}`}>{adminStats.views.no_questions.toLocaleString("ro-RO")}</div>
            <div className="text-xs text-slate-500 mt-0.5">{t("quiz.stat_noq", "Fără întrebări")}</div>
          </div>
        </div>
      )}

      {/* Quizzes Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && quizzes.some((q) => selectedIds.has(q.id)); }}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    aria-label={t("quiz.select_all", "Selectează tot")}
                  />
                </th>
                <th className="px-4 py-3">{t("quiz.col_quiz", "Quiz")}</th>
                <th className="px-4 py-3">{t("quiz.col_difficulty", "Dificultate")}</th>
                <th className="px-4 py-3 hidden lg:table-cell">{t("quiz.col_agegroup", "Grupă")}</th>
                <th className="px-4 py-3 text-center">{t("quiz.col_questions", "Întreb.")}</th>
                <th className="px-4 py-3 text-center">{t("quiz.col_points", "Puncte")}</th>
                <th className="px-4 py-3 text-center hidden md:table-cell">{t("quiz.col_attempts", "Încercări")}</th>
                <th className="px-4 py-3">{t("quiz.col_status", "Status")}</th>
                <th className="px-4 py-3 hidden xl:table-cell">{t("quiz.col_updated", "Actualizat")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center text-slate-400">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
                    {t("quiz.loading", "Se încarcă...")}
                  </td>
                </tr>
              ) : quizzes.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center">
                    <div className="text-slate-300 text-4xl mb-3">📝</div>
                    <p className="text-slate-600 font-medium">{t("quiz.none_found", "Niciun quiz găsit")}</p>
                    <p className="text-slate-400 text-xs mt-1">{t("quiz.none_found_hint", "Ajustează filtrele sau creează un quiz nou")}</p>
                  </td>
                </tr>
              ) : (
                quizzes.map((quiz) => {
                  const dm = DIFF_META[quiz.difficulty] || DIFF_META.easy;
                  const pts = quiz.max_points ?? quiz.reward_points;
                  const selected = selectedIds.has(quiz.id);
                  return (
                    <tr key={quiz.id} className={`group transition-colors ${selected ? "bg-indigo-50/60" : "hover:bg-slate-50"}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelect(quiz.id)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          aria-label={t("quiz.select_one", "Selectează")}
                        />
                      </td>
                      <td className="px-4 py-3 max-w-[360px]">
                        {(() => {
                          const cv = categoryVisual(quiz.category);
                          return (
                            <div className="flex items-center gap-3 min-w-0">
                              <div
                                className="w-9 h-9 rounded-xl shrink-0 grid place-items-center text-white text-sm font-bold overflow-hidden"
                                style={{ backgroundColor: cv.color }}
                              >
                                {cv.image ? <img src={cv.image} alt="" className="w-full h-full object-cover" /> : cv.letter}
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-slate-800 truncate">{quiz.title}</div>
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className="text-xs text-slate-400 truncate">{categoryTitle(quiz.category)}</span>
                                  {quiz.theme?.title && (
                                    <span className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-700 border border-violet-100 text-[11px] font-medium max-w-[140px]" title={quiz.theme.title}>
                                      <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" /></svg>
                                      <span className="truncate">{quiz.theme.title}</span>
                                    </span>
                                  )}
                                  {(quiz as any).free_included && (
                                    <span className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100 text-[11px] font-bold" title={t("quiz.free_included", "Inclus în Free?")}>
                                      <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                      Free
                                    </span>
                                  )}
                                </div>
                                {rowActions(quiz)}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${dm.cls}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />{dm.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {quiz.age_group ? (
                          <span className="text-xs font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-md px-2 py-0.5">{quiz.age_group}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {quiz.question_count ? (
                          <span className="tabular-nums text-slate-700">{quiz.question_count}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-600 font-medium" title={t("quiz.no_questions_warn", "Fără întrebări")}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" /></svg>0
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 tabular-nums text-slate-700 font-medium" title={t("quiz.points_hint", "Puncte pe care le conține quiz-ul (suma punctelor întrebărilor)")}>
                          <span className="text-amber-500">★</span>{pts}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center hidden md:table-cell">
                        <span className="tabular-nums text-slate-500">{(quiz.attempt_count || 0).toLocaleString("ro-RO")}</span>
                      </td>
                      <td className="px-4 py-3">
                        {quiz.is_active ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{t("quiz.published", "Publicat")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                            {t("quiz.draft", "Ciornă")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell text-slate-500 whitespace-nowrap text-xs">
                        {new Date(quiz.updated_at).toLocaleDateString("ro-RO", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          {t("quiz.rows_per_page", "Rânduri")}
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="text-sm text-slate-500">
          {total > 0 ? (
            <>
              <span className="tabular-nums font-semibold text-slate-700">{((page - 1) * pageSize + 1).toLocaleString("ro-RO")}–{Math.min(page * pageSize, total).toLocaleString("ro-RO")}</span>{" "}
              {t("quiz.of", "din")}{" "}
              <span className="tabular-nums font-semibold text-slate-700">{total.toLocaleString("ro-RO")}</span>
            </>
          ) : "—"}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="min-w-[34px] h-[34px] px-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-default hover:border-slate-300"
          >
            ‹
          </button>
          {pageWindow().map((p, i) =>
            p === "…" ? (
              <span key={`dots-${i}`} className="px-2 text-slate-400">…</span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`min-w-[34px] h-[34px] px-2 rounded-lg text-sm font-medium border ${
                  p === page ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
              >
                {p}
              </button>
            )
          )}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="min-w-[34px] h-[34px] px-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-default hover:border-slate-300"
          >
            ›
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-40 flex items-center gap-2 bg-slate-900 text-white rounded-2xl shadow-2xl pl-4 pr-2 py-2">
          <span className="text-sm font-semibold whitespace-nowrap">
            <span className="text-indigo-300">{selectedIds.size}</span> {t("quiz.selected", "selectate")}
          </span>
          <span className="w-px h-5 bg-white/20 mx-1" />
          <button disabled={bulkBusy} onClick={() => bulkSetActive(true)} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 hover:bg-white/20 disabled:opacity-50">
            {t("quiz.bulk_publish", "Publică")}
          </button>
          <button disabled={bulkBusy} onClick={() => bulkSetActive(false)} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 hover:bg-white/20 disabled:opacity-50">
            {t("quiz.bulk_draft", "Trece în ciornă")}
          </button>
          <select
            disabled={bulkBusy}
            value=""
            onChange={(e) => {
              const v = e.target.value;
              e.target.value = "";
              if (v) bulkSetCategory(v);
            }}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white [&>option]:text-slate-800 cursor-pointer"
            title={t("quiz.bulk_move_category", "Mută în categorie")}
          >
            <option value="">{t("quiz.bulk_move_category", "Mută în categorie")}…</option>
            {quizCategories.map((cat) => (
              <option key={cat.id} value={cat.name}>{cat.title}</option>
            ))}
          </select>
          <button disabled={bulkBusy} onClick={bulkDelete} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 hover:bg-rose-600 disabled:opacity-50">
            {t("quiz.bulk_delete", "Șterge")}
          </button>
          <button onClick={clearSelection} className="w-8 h-8 grid place-items-center rounded-lg text-slate-400 hover:bg-white/10 hover:text-white" aria-label={t("common.close", "Închide")}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Create Quiz Modal */}
      {showCreateQuizModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-modal-in border border-slate-200">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Creează quiz</h2>
            <form onSubmit={handleCreateQuiz}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t("quiz.category", "Categorie")}</label>
                  <select
                    value={quizForm.category}
                    onChange={(e) => setQuizForm({ ...quizForm, category: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">{t("quiz.select_category", "Selectează categoria...")}</option>
                    {quizCategories.map((cat) => (
                      <option key={cat.id} value={cat.name}>{cat.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t("quiz.theme", "Temă")}</label>
                  <select
                    value={quizForm.theme_id}
                    onChange={(e) => setQuizForm({ ...quizForm, theme_id: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">{t("quiz.select_theme", "Selectează tema...")}</option>
                    {quizThemes.map((theme) => (
                      <option key={theme.id} value={theme.id}>{theme.title}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <div className="flex items-end gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t("quiz.theme_order", "Ordine în temă")}
                      </label>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quizForm.theme_order}
                        disabled={!quizForm.theme_id}
                        onChange={(e) => {
                          const v = e.target.value;
                          const n = parseInt(v, 10);
                          setQuizForm({ ...quizForm, theme_order: v === "" || Number.isNaN(n) ? "" : n });
                        }}
                        placeholder={t("quiz.theme_order_ph", "ex. 1, 2, 3…")}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>
                    {/* Free-plan inclusion — on the same row as the theme order. */}
                    <label className="flex items-center gap-2 pb-2.5 cursor-pointer whitespace-nowrap select-none">
                      <input
                        type="checkbox"
                        checked={quizForm.free_included}
                        onChange={(e) => setQuizForm({ ...quizForm, free_included: e.target.checked })}
                        className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-slate-700">{t("quiz.free_included", "Inclus în Free?")}</span>
                    </label>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {quizForm.theme_id
                      ? t("quiz.theme_order_hint", "Poziția quiz-ului în temă. Sportivii trebuie să finalizeze quiz-urile în această ordine (1, 2, 3…). Lasă gol dacă nu contează ordinea.")
                      : t("quiz.theme_order_disabled", "Alege întâi o temă pentru a seta ordinea.")}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {t("quiz.free_included_hint", "Bifat: quiz-ul poate fi jucat de sportivii cu plan Free. Ceilalți îl văd, dar apare blocat.")}
                  </p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Titlu *</label>
                  <input
                    type="text"
                    required
                    value={quizForm.title}
                    onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t("quiz.description", "Descriere")}</label>
                  <RichTextEditor
                    value={quizForm.description}
                    onChange={(value) => setQuizForm({ ...quizForm, description: value })}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("quiz.content", "Conținut")}
                    <span className="ml-2 text-xs font-normal text-slate-400">{t("quiz.contentHint", "material de învățare — nu afectează quiz-ul")}</span>
                  </label>
                  <RichTextEditor
                    value={quizForm.content}
                    onChange={(value) => setQuizForm({ ...quizForm, content: value })}
                    enableImages
                    imageFolder="quiz/content"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Dificultate</label>
                  <select
                    value={quizForm.difficulty}
                    onChange={(e) => setQuizForm({ ...quizForm, difficulty: e.target.value as any })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="easy">Ușor</option>
                    <option value="medium">Mediu</option>
                    <option value="hard">Greu</option>
                  </select>
                  {recommendedDifficultyFor(quizForm.age_group) && (
                    <p className="mt-1 text-xs text-indigo-600">
                      {t(
                        "quiz.difficulty_from_age_group",
                        "Setată automat din grupa de vârstă selectată. O poți modifica."
                      )}
                    </p>
                  )}
                </div>
                <SportTypeSelect
                  value={quizForm.sport_type ? quizForm.sport_type.split(",").map(s => s.trim()).filter(Boolean) : []}
                  onChange={(vals) => setQuizForm({ ...quizForm, sport_type: vals.join(", ") })}
                  label="Tip sport"
                  placeholder="Selectează tipuri de sport..."
                />
                <AgeGroupSelect
                  value={quizForm.age_group}
                  onChange={handleQuizAgeGroupChange}
                  label="Grupă de vârstă"
                  placeholder="Selectează grupe de vârstă..."
                  options={ageGroups.map((g) => g.name)}
                />
                {/* Banda de vârstă pentru care e SCRIS capitolul.
                    Diferită de „Grupă de vârstă": aceea țintește alocările,
                    asta decide cui i se arată capitolul în aplicație. Gol =
                    tuturor, ca tot conținutul existent. */}
                <label className="block">
                  <span className="block mb-1.5 text-xs font-semibold text-slate-600">
                    Scris pentru banda de vârstă
                  </span>
                  <select
                    value={quizForm.age_band}
                    onChange={(e) =>
                      setQuizForm({ ...quizForm, age_band: e.target.value as typeof quizForm.age_band })
                    }
                    className="w-full px-4 py-2.5 text-sm bg-white border rounded-xl border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Toate vârstele</option>
                    {(Object.keys(AGE_BANDS) as Array<keyof typeof AGE_BANDS>).map((b) => (
                      <option key={b} value={b}>
                        {AGE_BANDS[b].label}
                      </option>
                    ))}
                  </select>
                  <span className="block mt-1 text-[11px] leading-relaxed text-slate-400">
                    Sportivii din afara benzii nu văd capitolul. Cei fără dată de naștere îl văd oricum.
                  </span>
                </label>
                {/* Punctajul quiz-ului se calculează automat din suma punctelor
                    întrebărilor — nu se mai setează manual aici. */}

                {/* Speed-scoring card — timer + preview. Empty inputs
                    mean null server-side (no timer, auto preview
                    based on question word count). */}
                <div className="col-span-2 p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-blue-800 mb-1">Timer & citire (opțional)</p>
                    <p className="text-xs text-blue-600">
                      Un răspuns corect aduce mereu punctele întregi ale întrebării.
                      Viteza adaugă un bonus peste ele, până la 50% din bază:{" "}
                      <span className="font-mono">bonus = bază × 50% × (1 − timp / timer)</span>.
                      Gol = fără cronometru, deci și fără bonus de viteză.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">
                        Timp per întrebare (secunde)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="120"
                        placeholder="ex: 10"
                        value={quizForm.time_limit_seconds}
                        onChange={(e) => setQuizForm({
                          ...quizForm,
                          time_limit_seconds: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0),
                        })}
                        className="w-full px-3 py-2 bg-white border border-blue-200 rounded-lg text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">
                        Timp citire (secunde, gol = auto)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="30"
                        placeholder="auto"
                        value={quizForm.preview_seconds}
                        onChange={(e) => setQuizForm({
                          ...quizForm,
                          preview_seconds: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0),
                        })}
                        className="w-full px-3 py-2 bg-white border border-blue-200 rounded-lg text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Video Mode Section */}
                <div className="col-span-2 p-4 bg-purple-50 rounded-xl border border-purple-200">
                  <label className="block text-sm font-medium text-purple-700 mb-2">Mod video</label>
                  <div className="flex gap-3 mb-3">
                    {(["none", "full", "chapters"] as QuizVideoMode[]).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setQuizForm({ ...quizForm, video_mode: mode, video_chapters: mode === "chapters" ? quizForm.video_chapters : [] })}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          quizForm.video_mode === mode
                            ? "bg-purple-600 text-white shadow-lg shadow-purple-500/25"
                            : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {mode === "none" && "Fără video"}
                        {mode === "full" && "Video complet"}
                        {mode === "chapters" && "Capitole"}
                      </button>
                    ))}
                  </div>

                  {quizForm.video_mode !== "none" && (
                    <div className="space-y-4">
                      {/* Video Source Type */}
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-2">{t("quiz.video_source", "Sursă video")}</label>
                        <div className="flex gap-2">
                          {(["self_hosted", "youtube", "vimeo"] as const).map((source) => (
                            <button
                              key={source}
                              type="button"
                              onClick={() => {
                                setVideoSourceType(source);
                                setQuizForm({ ...quizForm, video_url: "" });
                              }}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                videoSourceType === source
                                  ? "bg-purple-600 text-white shadow-lg shadow-purple-500/25"
                                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                              }`}
                            >
                              {source === "self_hosted" && t("quiz.self_hosted", "Găzduit local")}
                              {source === "youtube" && "YouTube"}
                              {source === "vimeo" && "Vimeo"}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Self Hosted - Drag & Drop */}
                      {videoSourceType === "self_hosted" && (
                        <div
                          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                          onDragLeave={() => setIsDragging(false)}
                          onDrop={handleFileDrop}
                          onClick={() => fileInputRef.current?.click()}
                          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
                            isDragging
                              ? "border-purple-500 bg-purple-50"
                              : "border-purple-300 bg-white hover:border-purple-400 hover:bg-purple-50"
                          }`}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="video/*"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                          <svg className="w-12 h-12 mx-auto mb-3 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <p className="text-purple-700 font-medium">{t("quiz.drag_drop_video", "Trage și plasează videoul aici")}</p>
                          <p className="text-sm text-purple-500 mt-1">{t("quiz.or_click_browse", "sau dă clic pentru a răsfoi")}</p>
                          {quizForm.video_url && (
                            <p className="text-sm text-emerald-600 mt-3 font-medium">{quizForm.video_url}</p>
                          )}
                        </div>
                      )}

                      {/* YouTube/Vimeo URL Input */}
                      {(videoSourceType === "youtube" || videoSourceType === "vimeo") && (
                        <div>
                          <label className="block text-sm font-medium text-purple-700 mb-1">
                            {videoSourceType === "youtube" ? t("quiz.youtube_url", "URL YouTube") : t("quiz.vimeo_url", "URL Vimeo")} *
                          </label>
                          <input
                            type="url"
                            placeholder={videoSourceType === "youtube" ? "https://www.youtube.com/watch?v=..." : "https://vimeo.com/..."}
                            value={quizForm.video_url}
                            onChange={(e) => setQuizForm({ ...quizForm, video_url: e.target.value })}
                            className="w-full px-4 py-2.5 bg-white border border-purple-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                          />
                        </div>
                      )}

                      {/* Video Preview */}
                      {videoPreviewId && (videoSourceType === "youtube" || videoSourceType === "vimeo") && (
                        <div className="relative">
                          <label className="block text-sm font-medium text-purple-700 mb-2">{t("quiz.video_preview", "Previzualizare video")}</label>
                          <div className="aspect-video rounded-xl overflow-hidden border border-purple-200 bg-slate-900">
                            {videoSourceType === "youtube" ? (
                              <iframe
                                src={`https://www.youtube.com/embed/${videoPreviewId}`}
                                title="Previzualizare video YouTube"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="w-full h-full"
                              />
                            ) : (
                              <iframe
                                src={`https://player.vimeo.com/video/${videoPreviewId}`}
                                title="Previzualizare video Vimeo"
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                                className="w-full h-full"
                              />
                            )}
                          </div>
                          {fetchingDuration && (
                            <div className="absolute top-8 right-2 flex items-center gap-2 bg-white/90 px-3 py-1.5 rounded-lg shadow-sm">
                              <svg className="animate-spin h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="text-xs text-purple-700">{t("quiz.fetching_duration", "Se preia durata...")}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Duration - Minutes & Seconds */}
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-2">{t("quiz.video_duration", "Durată video")} *</label>
                        <div className="flex gap-3 items-center">
                          <div className="flex-1">
                            <label className="block text-xs text-purple-500 mb-1">{t("quiz.minutes", "Minute")}</label>
                            <input
                              type="number"
                              min="0"
                              value={videoDurationMinutes}
                              onChange={(e) => {
                                const mins = parseInt(e.target.value) || 0;
                                setVideoDurationMinutes(mins);
                                updateVideoDuration(mins, videoDurationSeconds);
                              }}
                              className="w-full px-4 py-2.5 bg-white border border-purple-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                            />
                          </div>
                          <span className="text-purple-500 font-bold mt-5">:</span>
                          <div className="flex-1">
                            <label className="block text-xs text-purple-500 mb-1">{t("quiz.seconds", "Secunde")}</label>
                            <input
                              type="number"
                              min="0"
                              max="59"
                              value={videoDurationSeconds}
                              onChange={(e) => {
                                const secs = Math.min(59, parseInt(e.target.value) || 0);
                                setVideoDurationSeconds(secs);
                                updateVideoDuration(videoDurationMinutes, secs);
                              }}
                              className="w-full px-4 py-2.5 bg-white border border-purple-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-purple-500 mb-1">{t("quiz.total_seconds", "Total (s)")}</label>
                            <div className="px-4 py-2.5 bg-purple-100 border border-purple-200 rounded-xl text-purple-700 font-medium">
                              {quizForm.video_duration || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {quizForm.video_mode === "chapters" && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-purple-700">{t("quiz.chapters", "Capitole")}</label>
                        {quizForm.video_duration > 0 && (
                          <span className="text-xs text-purple-500">
                            {t("quiz.max_duration", "Max")}: {quizForm.video_duration}s
                          </span>
                        )}
                      </div>
                      {quizForm.video_chapters.map((chapter, index) => (
                        <div key={index} className="p-3 mb-3 bg-white rounded-lg border border-purple-200">
                          <div className="flex gap-2 mb-2 items-start">
                            <div className="flex-1">
                              <label className="block text-xs text-purple-500 mb-1">{t("quiz.chapter_title", "Titlu")}</label>
                              <input
                                type="text"
                                placeholder={t("quiz.chapter_title", "Titlu capitol")}
                                value={chapter.title}
                                onChange={(e) => {
                                  const newChapters = [...quizForm.video_chapters];
                                  newChapters[index] = { ...chapter, title: e.target.value };
                                  setQuizForm({ ...quizForm, video_chapters: newChapters });
                                }}
                                className="w-full px-3 py-2 bg-slate-50 border border-purple-200 rounded-lg text-sm text-slate-800 placeholder-slate-400"
                              />
                            </div>
                            <div className="w-20">
                              <label className="block text-xs text-purple-500 mb-1">{t("quiz.from", "De la")} (s)</label>
                              <input
                                type="number"
                                min="0"
                                max={quizForm.video_duration || undefined}
                                placeholder="0"
                                value={chapter.start_time}
                                onChange={(e) => {
                                  const newChapters = [...quizForm.video_chapters];
                                  const value = Math.min(parseInt(e.target.value) || 0, quizForm.video_duration || Infinity);
                                  newChapters[index] = { ...chapter, start_time: value };
                                  setQuizForm({ ...quizForm, video_chapters: newChapters });
                                }}
                                className="w-full px-3 py-2 bg-slate-50 border border-purple-200 rounded-lg text-sm text-slate-800 placeholder-slate-400"
                              />
                            </div>
                            <div className="w-20">
                              <label className="block text-xs text-purple-500 mb-1">{t("quiz.to", "Până la")} (s)</label>
                              <input
                                type="number"
                                min="0"
                                max={quizForm.video_duration || undefined}
                                placeholder="60"
                                value={chapter.end_time}
                                onChange={(e) => {
                                  const newChapters = [...quizForm.video_chapters];
                                  const value = Math.min(parseInt(e.target.value) || 0, quizForm.video_duration || Infinity);
                                  newChapters[index] = { ...chapter, end_time: value };
                                  setQuizForm({ ...quizForm, video_chapters: newChapters });
                                }}
                                className={`w-full px-3 py-2 bg-slate-50 border rounded-lg text-sm text-slate-800 placeholder-slate-400 ${
                                  quizForm.video_duration > 0 && chapter.end_time > quizForm.video_duration
                                    ? 'border-red-400 bg-red-50'
                                    : 'border-purple-200'
                                }`}
                              />
                              {quizForm.video_duration > 0 && chapter.end_time > quizForm.video_duration && (
                                <span className="text-xs text-red-500 mt-1 block">{t("quiz.exceeds_duration", "Depășește durata")}</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const newChapters = quizForm.video_chapters.filter((_, i) => i !== index);
                                setQuizForm({ ...quizForm, video_chapters: newChapters });
                              }}
                              className="p-2 mt-5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              ✕
                            </button>
                          </div>
                          <div>
                            <label className="block text-xs text-purple-500 mb-1">{t("quiz.chapter_description", "Descriere")}</label>
                            <textarea
                              placeholder={t("quiz.chapter_description_placeholder", "Descriere opțională...")}
                              value={chapter.description || ""}
                              onChange={(e) => {
                                const newChapters = [...quizForm.video_chapters];
                                newChapters[index] = { ...chapter, description: e.target.value };
                                setQuizForm({ ...quizForm, video_chapters: newChapters });
                              }}
                              rows={2}
                              className="w-full px-3 py-2 bg-slate-50 border border-purple-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 resize-none"
                            />
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const lastEnd = quizForm.video_chapters.length > 0
                            ? quizForm.video_chapters[quizForm.video_chapters.length - 1].end_time
                            : 0;
                          const newEnd = Math.min(lastEnd + 60, quizForm.video_duration || lastEnd + 60);
                          setQuizForm({
                            ...quizForm,
                            video_chapters: [...quizForm.video_chapters, { title: `${t("quiz.chapter", "Capitol")} ${quizForm.video_chapters.length + 1}`, description: "", start_time: lastEnd, end_time: newEnd }],
                          });
                        }}
                        className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium transition-colors"
                      >
                        + {t("quiz.add_chapter", "Adaugă capitol")}
                      </button>
                    </div>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("quiz.status", "Stare")}</label>
                  <div className="inline-flex rounded-xl border border-slate-200 p-1 bg-slate-50">
                    <button
                      type="button"
                      onClick={() => setQuizForm({ ...quizForm, is_active: false })}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        !quizForm.is_active ? "bg-white text-slate-800 shadow-sm border border-slate-200" : "text-slate-500"
                      }`}
                    >
                      {t("quiz.draft", "Ciornă")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuizForm({ ...quizForm, is_active: true })}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        quizForm.is_active ? "bg-emerald-600 text-white shadow-sm" : "text-slate-500"
                      }`}
                    >
                      {t("quiz.published", "Publicat")}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">
                    {t("quiz.draft_hint", "Ciorna e ascunsă de sportivi până o publici.")}
                  </p>
                </div>
              </div>
              <div className="sticky bottom-0 -mx-6 -mb-6 mt-6 px-6 py-3 bg-slate-100 border-t border-slate-200 flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateQuizModal(false);
                    resetQuizForm();
                  }}
                  className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-sm"
                >
                  {quizForm.is_active ? t("quiz.create_publish", "Creează și publică") : t("quiz.create_draft", "Salvează ca ciornă")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assignments Modal */}
      {showAssignmentsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-modal-in border border-slate-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-800">
                  {t("admin.quizManagement.assignments", "Programări quiz")}
                </h2>
                <button
                  onClick={() => {
                    setShowAssignmentsModal(false);
                    setSelectedQuiz(null);
                    resetAssignmentForm();
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-all duration-200"
                >
                  ✕
                </button>
              </div>

              {selectedQuiz && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-slate-800 mb-1">{selectedQuiz.title}</h3>
                  <p className="text-sm text-slate-500">Atribuie acest quiz grupelor de vârstă</p>
                </div>
              )}

              {/* Create Assignment Form */}
              <form onSubmit={handleCreateAssignment} className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <h4 className="text-md font-medium text-slate-800 mb-4">Creează programare nouă</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AgeGroupSelect
                    value={assignmentForm.age_group}
                    onChange={(vals) => setAssignmentForm({ ...assignmentForm, age_group: vals })}
                    label="Grupă de vârstă *"
                    placeholder="Selectează grupa de vârstă..."
                  />
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Quiz-uri pe săptămână</label>
                    <input
                      type="number"
                      min="1"
                      value={assignmentForm.quizzes_per_week}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, quizzes_per_week: parseInt(e.target.value) || 1 })}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Data de început *</label>
                    <DateInput
                      required
                      value={assignmentForm.start_date}
                      onChange={(v) => setAssignmentForm({ ...assignmentForm, start_date: v })}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Data de sfârșit <span className="text-xs text-slate-400">(opțional)</span>
                    </label>
                    <DateInput
                      value={assignmentForm.end_date}
                      onChange={(v) => setAssignmentForm({ ...assignmentForm, end_date: v })}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  <input
                    type="checkbox"
                    id="assignment_active"
                    checked={assignmentForm.is_active}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-amber-600 bg-white border-slate-300 rounded focus:ring-amber-500 transition-colors"
                  />
                  <label htmlFor="assignment_active" className="ml-2 text-sm text-slate-700">Activ</label>
                </div>
                <button
                  type="submit"
                  className="mt-4 px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-all duration-200 font-medium shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40"
                >
                  Creează programare
                </button>
              </form>

              {/* Existing Assignments List */}
              <div>
                <h4 className="text-md font-medium text-slate-800 mb-4">Programări existente</h4>
                {assignments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-xl border border-slate-200">
                    Încă nu există programări. Creează una mai sus.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {assignments.map((assignment) => (
                      <div
                        key={assignment.id}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-amber-300 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-lg">
                                {assignment.age_group}
                              </span>
                              {!assignment.is_active && (
                                <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-lg">
                                  Inactiv
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-slate-600 space-y-1">
                              <div>{assignment.quizzes_per_week} quiz-uri pe săptămână</div>
                              <div>Început: {formatDate(new Date(assignment.start_date))}</div>
                              {assignment.end_date && (
                                <div>Sfârșit: {formatDate(new Date(assignment.end_date))}</div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteAssignment(assignment.id)}
                            className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm transition-all duration-200 font-medium"
                          >
                            Șterge
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Quiz Modal */}
      {showEditQuizModal && selectedQuiz && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-modal-in border border-slate-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-800">Editează quiz</h2>
                <button
                  onClick={() => {
                    setShowEditQuizModal(false);
                    setSelectedQuiz(null);
                    resetQuizForm();
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-all duration-200"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleUpdateQuiz}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t("quiz.category", "Categorie")}</label>
                      <select
                        value={quizForm.category}
                        onChange={(e) => setQuizForm({ ...quizForm, category: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      >
                        <option value="">{t("quiz.select_category", "Selectează categoria...")}</option>
                        {quizCategories.map((cat) => (
                          <option key={cat.id} value={cat.name}>{cat.title}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t("quiz.theme", "Temă")}</label>
                      <select
                        value={quizForm.theme_id}
                        onChange={(e) => setQuizForm({ ...quizForm, theme_id: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      >
                        <option value="">{t("quiz.select_theme", "Selectează tema...")}</option>
                        {quizThemes.map((theme) => (
                          <option key={theme.id} value={theme.id}>{theme.title}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-end gap-4">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {t("quiz.theme_order", "Ordine în temă")}
                          </label>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={quizForm.theme_order}
                            disabled={!quizForm.theme_id}
                            onChange={(e) => {
                              const v = e.target.value;
                              const n = parseInt(v, 10);
                              setQuizForm({ ...quizForm, theme_order: v === "" || Number.isNaN(n) ? "" : n });
                            }}
                            placeholder={t("quiz.theme_order_ph", "ex. 1, 2, 3…")}
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                        <label className="flex items-center gap-2 pb-2.5 cursor-pointer whitespace-nowrap select-none">
                          <input
                            type="checkbox"
                            checked={quizForm.free_included}
                            onChange={(e) => setQuizForm({ ...quizForm, free_included: e.target.checked })}
                            className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-slate-700">{t("quiz.free_included", "Inclus în Free?")}</span>
                        </label>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {quizForm.theme_id
                          ? t("quiz.theme_order_hint", "Poziția quiz-ului în temă. Sportivii trebuie să finalizeze quiz-urile în această ordine (1, 2, 3…). Lasă gol dacă nu contează ordinea.")
                          : t("quiz.theme_order_disabled", "Alege întâi o temă pentru a seta ordinea.")}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {t("quiz.free_included_hint", "Bifat: quiz-ul poate fi jucat de sportivii cu plan Free. Ceilalți îl văd, dar apare blocat.")}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Titlu *</label>
                    <input
                      type="text"
                      required
                      value={quizForm.title}
                      onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t("quiz.description", "Descriere")}</label>
                    <RichTextEditor
                      value={quizForm.description}
                      onChange={(value) => setQuizForm({ ...quizForm, description: value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t("quiz.content", "Conținut")}
                      <span className="ml-2 text-xs font-normal text-slate-400">{t("quiz.contentHint", "material de învățare — nu afectează quiz-ul")}</span>
                    </label>
                    <RichTextEditor
                      value={quizForm.content}
                      onChange={(value) => setQuizForm({ ...quizForm, content: value })}
                      enableImages
                      imageFolder="quiz/content"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Dificultate</label>
                    <select
                      value={quizForm.difficulty}
                      onChange={(e) => setQuizForm({ ...quizForm, difficulty: e.target.value as any })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="easy">Ușor</option>
                      <option value="medium">Mediu</option>
                      <option value="hard">Greu</option>
                    </select>
                    {recommendedDifficultyFor(quizForm.age_group) && (
                      <p className="mt-1 text-xs text-indigo-600">
                        {t(
                          "quiz.difficulty_from_age_group",
                          "Setată automat din grupa de vârstă selectată. O poți modifica."
                        )}
                      </p>
                    )}
                  </div>

                  {/* Sport Type, Age Group, Reward Points */}
                  <div className="grid grid-cols-2 gap-4">
                    <SportTypeSelect
                      value={quizForm.sport_type ? quizForm.sport_type.split(",").map(s => s.trim()).filter(Boolean) : []}
                      onChange={(vals) => setQuizForm({ ...quizForm, sport_type: vals.join(", ") })}
                      label="Tip sport"
                      placeholder="Selectează tipuri de sport..."
                    />
                    <AgeGroupSelect
                      value={quizForm.age_group}
                      onChange={handleQuizAgeGroupChange}
                      label="Grupă de vârstă"
                      placeholder="Selectează grupe de vârstă..."
                      options={ageGroups.map((g) => g.name)}
                    />
                    {/* Vezi nota din modalul de creare. */}
                    <label className="block">
                      <span className="block mb-1.5 text-xs font-semibold text-slate-600">
                        Scris pentru banda de vârstă
                      </span>
                      <select
                        value={quizForm.age_band}
                        onChange={(e) =>
                          setQuizForm({ ...quizForm, age_band: e.target.value as typeof quizForm.age_band })
                        }
                        className="w-full px-4 py-2.5 text-sm bg-white border rounded-xl border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Toate vârstele</option>
                        {(Object.keys(AGE_BANDS) as Array<keyof typeof AGE_BANDS>).map((b) => (
                          <option key={b} value={b}>
                            {AGE_BANDS[b].label}
                          </option>
                        ))}
                      </select>
                      <span className="block mt-1 text-[11px] leading-relaxed text-slate-400">
                        Sportivii din afara benzii nu văd capitolul. Cei fără dată de naștere îl văd oricum.
                      </span>
                    </label>
                    <div>
                      <p className="text-sm font-medium text-slate-700">
                        Total puncte quiz — <span className="font-semibold tabular-nums">{(selectedQuiz?.questions || []).reduce((s, q) => s + (q.points || 1), 0)} puncte</span>
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Calculat automat din suma punctelor întrebărilor.</p>
                    </div>
                  </div>

                  {/* Speed-scoring — mirrors the create modal */}
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-blue-800 mb-1">Timer & citire (opțional)</p>
                      <p className="text-xs text-blue-600">
                        Un răspuns corect aduce mereu punctele întregi ale întrebării; viteza
                        adaugă un bonus peste ele. Gol = fără cronometru, deci și fără bonus.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">Timp per întrebare (secunde)</label>
                        <input
                          type="number"
                          min="0"
                          max="120"
                          placeholder="ex: 10"
                          value={quizForm.time_limit_seconds}
                          onChange={(e) => setQuizForm({
                            ...quizForm,
                            time_limit_seconds: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0),
                          })}
                          className="w-full px-3 py-2 bg-white border border-blue-200 rounded-lg text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">Timp citire (secunde, gol = auto)</label>
                        <input
                          type="number"
                          min="0"
                          max="30"
                          placeholder="auto"
                          value={quizForm.preview_seconds}
                          onChange={(e) => setQuizForm({
                            ...quizForm,
                            preview_seconds: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0),
                          })}
                          className="w-full px-3 py-2 bg-white border border-blue-200 rounded-lg text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Video Mode Section */}
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <label className="block text-sm font-medium text-purple-700 mb-2">Mod video</label>
                    <div className="flex gap-3 mb-3">
                      {(["none", "full", "chapters"] as QuizVideoMode[]).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setQuizForm({ ...quizForm, video_mode: mode, video_chapters: mode === "chapters" ? quizForm.video_chapters : [] })}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            quizForm.video_mode === mode
                              ? "bg-purple-600 text-white shadow-lg shadow-purple-500/25"
                              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          {mode === "none" && "No Video"}
                          {mode === "full" && "Full Video"}
                          {mode === "chapters" && "Chapters"}
                        </button>
                      ))}
                    </div>

                    {quizForm.video_mode !== "none" && (
                      <div className="space-y-3">
                        {/* Video Source Type */}
                        <div>
                          <label className="block text-sm font-medium text-purple-700 mb-1">{t("quiz.video_source", "Sursă video")}</label>
                          <div className="flex gap-2">
                            {(["self_hosted", "youtube", "vimeo"] as const).map((source) => (
                              <button
                                key={source}
                                type="button"
                                onClick={() => {
                                  setVideoSourceType(source);
                                  setQuizForm({ ...quizForm, video_url: "" });
                                  setVideoPreviewId(null);
                                }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  videoSourceType === source
                                    ? "bg-purple-600 text-white shadow-lg shadow-purple-500/25"
                                    : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                                }`}
                              >
                                {source === "self_hosted" && t("quiz.self_hosted", "Găzduit local")}
                                {source === "youtube" && "YouTube"}
                                {source === "vimeo" && "Vimeo"}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Self Hosted URL */}
                        {videoSourceType === "self_hosted" && (
                          <div>
                            <label className="block text-sm font-medium text-purple-700 mb-1">URL video *</label>
                            <input
                              type="url"
                              placeholder="https://example.com/video.mp4"
                              value={quizForm.video_url}
                              onChange={(e) => setQuizForm({ ...quizForm, video_url: e.target.value })}
                              className="w-full px-4 py-2.5 bg-white border border-purple-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                            />
                          </div>
                        )}

                        {/* YouTube/Vimeo URL */}
                        {(videoSourceType === "youtube" || videoSourceType === "vimeo") && (
                          <div>
                            <label className="block text-sm font-medium text-purple-700 mb-1">
                              {videoSourceType === "youtube" ? t("quiz.youtube_url", "URL YouTube") : t("quiz.vimeo_url", "URL Vimeo")} *
                            </label>
                            <input
                              type="url"
                              placeholder={videoSourceType === "youtube" ? "https://www.youtube.com/watch?v=..." : "https://vimeo.com/..."}
                              value={quizForm.video_url}
                              onChange={(e) => setQuizForm({ ...quizForm, video_url: e.target.value })}
                              className="w-full px-4 py-2.5 bg-white border border-purple-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                            />
                          </div>
                        )}

                        {/* Video Preview */}
                        {videoPreviewId && (videoSourceType === "youtube" || videoSourceType === "vimeo") && (
                          <div className="relative">
                            <label className="block text-sm font-medium text-purple-700 mb-2">{t("quiz.video_preview", "Previzualizare video")}</label>
                            <div className="aspect-video rounded-xl overflow-hidden border border-purple-200 bg-slate-900">
                              {videoSourceType === "youtube" ? (
                                <iframe
                                  src={`https://www.youtube.com/embed/${videoPreviewId}`}
                                  title="Previzualizare video YouTube"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  className="w-full h-full"
                                />
                              ) : (
                                <iframe
                                  src={`https://player.vimeo.com/video/${videoPreviewId}`}
                                  title="Previzualizare video Vimeo"
                                  allow="autoplay; fullscreen; picture-in-picture"
                                  allowFullScreen
                                  className="w-full h-full"
                                />
                              )}
                            </div>
                            {fetchingDuration && (
                              <div className="absolute top-8 right-2 flex items-center gap-2 bg-white/90 px-3 py-1.5 rounded-lg shadow-sm">
                                <svg className="animate-spin h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="text-xs text-purple-700">{t("quiz.fetching_duration", "Se preia durata...")}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Video Duration */}
                        <div>
                          <label className="block text-sm font-medium text-purple-700 mb-2">{t("quiz.video_duration", "Durată video")} *</label>
                          <div className="flex gap-3 items-center">
                            <div className="flex-1">
                              <label className="block text-xs text-purple-500 mb-1">{t("quiz.minutes", "Minute")}</label>
                              <input
                                type="number"
                                min="0"
                                value={videoDurationMinutes}
                                onChange={(e) => {
                                  const mins = parseInt(e.target.value) || 0;
                                  setVideoDurationMinutes(mins);
                                  updateVideoDuration(mins, videoDurationSeconds);
                                }}
                                className="w-full px-4 py-2.5 bg-white border border-purple-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                              />
                            </div>
                            <span className="text-purple-500 font-bold mt-5">:</span>
                            <div className="flex-1">
                              <label className="block text-xs text-purple-500 mb-1">{t("quiz.seconds", "Secunde")}</label>
                              <input
                                type="number"
                                min="0"
                                max="59"
                                value={videoDurationSeconds}
                                onChange={(e) => {
                                  const secs = Math.min(59, parseInt(e.target.value) || 0);
                                  setVideoDurationSeconds(secs);
                                  updateVideoDuration(videoDurationMinutes, secs);
                                }}
                                className="w-full px-4 py-2.5 bg-white border border-purple-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-xs text-purple-500 mb-1">{t("quiz.total_seconds", "Total (s)")}</label>
                              <div className="px-4 py-2.5 bg-purple-100 border border-purple-200 rounded-xl text-purple-700 font-medium">
                                {quizForm.video_duration || 0}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {quizForm.video_mode === "chapters" && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-purple-700 mb-2">Capitole</label>
                        {quizForm.video_chapters.map((chapter, index) => (
                          <div key={index} className="flex gap-2 mb-2 items-center">
                            <input
                              type="text"
                              placeholder="Titlu capitol"
                              value={chapter.title}
                              onChange={(e) => {
                                const newChapters = [...quizForm.video_chapters];
                                newChapters[index] = { ...chapter, title: e.target.value };
                                setQuizForm({ ...quizForm, video_chapters: newChapters });
                              }}
                              className="flex-1 px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm text-slate-800 placeholder-slate-400"
                            />
                            <input
                              type="number"
                              placeholder="Început (s)"
                              value={chapter.start_time}
                              onChange={(e) => {
                                const newChapters = [...quizForm.video_chapters];
                                newChapters[index] = { ...chapter, start_time: parseInt(e.target.value) || 0 };
                                setQuizForm({ ...quizForm, video_chapters: newChapters });
                              }}
                              className="w-24 px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm text-slate-800 placeholder-slate-400"
                            />
                            <input
                              type="number"
                              placeholder="Sfârșit (s)"
                              value={chapter.end_time}
                              onChange={(e) => {
                                const newChapters = [...quizForm.video_chapters];
                                newChapters[index] = { ...chapter, end_time: parseInt(e.target.value) || 0 };
                                setQuizForm({ ...quizForm, video_chapters: newChapters });
                              }}
                              className="w-24 px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm text-slate-800 placeholder-slate-400"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newChapters = quizForm.video_chapters.filter((_, i) => i !== index);
                                setQuizForm({ ...quizForm, video_chapters: newChapters });
                              }}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const lastEnd = quizForm.video_chapters.length > 0
                              ? quizForm.video_chapters[quizForm.video_chapters.length - 1].end_time
                              : 0;
                            setQuizForm({
                              ...quizForm,
                              video_chapters: [...quizForm.video_chapters, { title: `Capitol ${quizForm.video_chapters.length + 1}`, start_time: lastEnd, end_time: lastEnd + 60 }],
                            });
                          }}
                          className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium transition-colors"
                        >
                          + Adaugă capitol
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("quiz.status", "Stare")}</label>
                    <div className="inline-flex rounded-xl border border-slate-200 p-1 bg-slate-50">
                      <button
                        type="button"
                        onClick={() => setQuizForm({ ...quizForm, is_active: false })}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          !quizForm.is_active ? "bg-white text-slate-800 shadow-sm border border-slate-200" : "text-slate-500"
                        }`}
                      >
                        {t("quiz.draft", "Ciornă")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setQuizForm({ ...quizForm, is_active: true })}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          quizForm.is_active ? "bg-emerald-600 text-white shadow-sm" : "text-slate-500"
                        }`}
                      >
                        {t("quiz.published", "Publicat")}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5">
                      {t("quiz.draft_hint", "Ciorna e ascunsă de sportivi până o publici.")}
                    </p>
                  </div>
                </div>

                <div className="sticky bottom-0 -mx-6 -mb-6 mt-6 px-6 py-3 bg-slate-100 border-t border-slate-200 flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditQuizModal(false);
                      setSelectedQuiz(null);
                      resetQuizForm();
                    }}
                    className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                  >
                    Anulează
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-sm"
                  >
                    Salvează modificările
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Quiz Modal */}
      {/* Per-quiz stats modal. Funnel: views → started → completed,
          + total attempts, avg score, last-completed-at, and how many
          schedule drops the quiz is currently part of. */}
      {/* Per-question analytics modal */}
      {showAnalyticsModal && analyticsForQuiz && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowAnalyticsModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Analiză pe întrebări</h2>
                <p className="text-sm text-slate-500 truncate max-w-md">{analyticsForQuiz.title}</p>
              </div>
              <button onClick={() => setShowAnalyticsModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {analyticsLoading ? (
                <div className="py-12 text-center text-slate-500">Se încarcă…</div>
              ) : !analyticsData ? (
                <div className="py-12 text-center text-slate-500">Fără date.</div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <div className="text-2xl font-extrabold text-slate-800">{analyticsData.quiz.total_attempts}</div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Încercări</div>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <div className="text-2xl font-extrabold text-slate-800">{analyticsData.quiz.completed_attempts}</div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Finalizate</div>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <div className="text-2xl font-extrabold text-slate-800">
                        {analyticsData.quiz.overall_accuracy_pct ?? "–"}{analyticsData.quiz.overall_accuracy_pct != null ? "%" : ""}
                      </div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Acuratețe</div>
                    </div>
                  </div>

                  {analyticsData.quiz.total_answers === 0 && (
                    <p className="text-sm text-slate-500 text-center py-6">
                      Încă nu s-a răspuns la acest quiz — analizele apar după primele încercări.
                    </p>
                  )}

                  <div className="space-y-4">
                    {analyticsData.questions.map((q, i) => {
                      const flagMeta = ({
                        healthy: { label: "Sănătoasă", cls: "bg-emerald-100 text-emerald-700" },
                        hard: { label: "Prea grea", cls: "bg-red-100 text-red-700" },
                        easy: { label: "Prea ușoară", cls: "bg-slate-100 text-slate-500" },
                        ambiguous: { label: "Posibil ambiguă", cls: "bg-amber-100 text-amber-700" },
                        low_data: { label: "Date puține", cls: "bg-slate-100 text-slate-400" },
                      } as const)[q.flag];
                      const barColor = q.p_value == null ? "#cbd5e1" : q.p_value < 30 ? "#ef4444" : q.p_value > 95 ? "#94a3b8" : "#10b981";
                      return (
                        <div key={q.id} className="border border-slate-100 rounded-xl p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-800">{i + 1}. {q.text}</p>
                              <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400">
                                <span className="uppercase font-bold">{q.kind === "single" ? "Unic" : q.kind === "multi" ? "Multiplu" : "A/F"}</span>
                                <span>· {q.times_answered} răspunsuri</span>
                                {q.avg_response_ms != null && <span>· {(q.avg_response_ms / 1000).toFixed(1)}s mediu</span>}
                              </div>
                            </div>
                            <span className={`shrink-0 text-[10.5px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full ${flagMeta.cls}`}>
                              {flagMeta.label}
                            </span>
                          </div>

                          <div className="mt-3">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-slate-500 font-semibold">Corect</span>
                              <span className="font-extrabold text-slate-700">{q.p_value != null ? `${q.p_value}%` : "–"}</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${q.p_value ?? 0}%`, backgroundColor: barColor }} />
                            </div>
                          </div>

                          {q.distribution.length > 0 && q.times_answered > 0 && (
                            <div className="mt-3 space-y-1.5">
                              {q.distribution.map((d, di) => {
                                const share = q.times_answered ? Math.round((d.count / q.times_answered) * 100) : 0;
                                return (
                                  <div key={di} className="flex items-center gap-2">
                                    <span className={`w-36 truncate text-xs ${d.is_correct ? "text-emerald-700 font-bold" : "text-slate-600"}`}>
                                      {d.is_correct ? "✓ " : ""}{d.label}
                                    </span>
                                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div className="h-full rounded-full" style={{ width: `${share}%`, backgroundColor: d.is_correct ? "#10b981" : "#cbd5e1" }} />
                                    </div>
                                    <span className="w-8 text-right text-[11px] tabular-nums text-slate-400">{d.count}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showStatsModal && statsForQuiz && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowStatsModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Statistici quiz</h2>
                <p className="text-sm text-slate-500 truncate max-w-md">{statsForQuiz.title}</p>
              </div>
              <button
                onClick={() => setShowStatsModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {statsLoading ? (
                <div className="text-center py-12 text-slate-500">Se încarcă...</div>
              ) : !statsData ? (
                <div className="text-center py-12 text-slate-400">Nu s-au putut încărca statisticile.</div>
              ) : (
                <>
                  {/* Funnel */}
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <p className="text-xs text-blue-600 mb-1">Văzut</p>
                      <p className="text-2xl font-bold text-blue-700">{statsData.views}</p>
                      <p className="text-xs text-blue-500 mt-1">utilizatori distincți</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-xl">
                      <p className="text-xs text-amber-600 mb-1">Început</p>
                      <p className="text-2xl font-bold text-amber-700">{statsData.started}</p>
                      <p className="text-xs text-amber-500 mt-1">au atins ≥1 încercare</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-xl">
                      <p className="text-xs text-emerald-600 mb-1">Finalizat</p>
                      <p className="text-2xl font-bold text-emerald-700">{statsData.completed}</p>
                      <p className="text-xs text-emerald-500 mt-1">
                        {statsData.started > 0
                          ? `${Math.round((statsData.completed / statsData.started) * 100)}% rate`
                          : "—"}
                      </p>
                    </div>
                  </div>

                  {/* Secondary numbers */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 border border-slate-200 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">Total încercări</p>
                      <p className="text-lg font-semibold text-slate-800">{statsData.total_attempts}</p>
                      <p className="text-xs text-slate-400">
                        din care {statsData.total_completed_attempts} complete
                      </p>
                    </div>
                    <div className="p-3 border border-slate-200 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">Scor mediu</p>
                      <p className="text-lg font-semibold text-slate-800">{statsData.avg_score}</p>
                      <p className="text-xs text-slate-400">pe încercări complete</p>
                    </div>
                    <div className="p-3 border border-slate-200 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">Puncte distribuite</p>
                      <p className="text-lg font-semibold text-emerald-600">
                        {statsData.total_points_awarded}p
                      </p>
                      <p className="text-xs text-slate-400">cumulat</p>
                    </div>
                    <div className="p-3 border border-slate-200 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">În programări</p>
                      <p className="text-lg font-semibold text-slate-800">
                        {statsData.schedule_drop_count}
                      </p>
                      <p className="text-xs text-slate-400">drops totale</p>
                    </div>
                  </div>

                  {statsData.last_completed_at && (
                    <p className="text-xs text-slate-400 text-center mt-6">
                      Ultima finalizare:{" "}
                      {new Date(statsData.last_completed_at).toLocaleString("ro-RO", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteQuizModal && selectedQuiz && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 p-6 animate-modal-in">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Șterge quiz</h2>
            <p className="text-slate-600 mb-6">
              Sigur vrei să ștergi <strong className="text-slate-800">{selectedQuiz.title}</strong>? Această acțiune nu poate fi anulată.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteQuizModal(false);
                  setSelectedQuiz(null);
                }}
                className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={handleDeleteQuiz}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-medium shadow-lg shadow-red-500/25 hover:shadow-red-500/40"
              >
                Șterge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Questions Modal */}
      {showQuestionsModal && selectedQuiz && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-modal-in border border-slate-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-800">
                  Întrebări: {selectedQuiz.title}
                </h2>
                <button
                  onClick={() => {
                    setShowQuestionsModal(false);
                    setSelectedQuiz(null);
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-all duration-200"
                >
                  ✕
                </button>
              </div>

              <button
                onClick={() => setShowAddQuestionModal(true)}
                className="mb-4 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-medium shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
              >
                + Adaugă întrebare
              </button>

              {selectedQuiz.questions && selectedQuiz.questions.length === 0 ? (
                <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-xl border border-slate-200">
                  Încă nu există întrebări. Adaugă una mai sus.
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedQuiz.questions?.map((question, index) => (
                    <div
                      key={question.id}
                      className={`p-4 rounded-xl border transition-all duration-200 ${
                        question.chapter_index !== null && question.chapter_index !== undefined
                          ? "bg-amber-50 border-amber-200 hover:border-amber-400"
                          : question.media_type === "video"
                          ? "bg-purple-50 border-purple-200 hover:border-purple-400"
                          : question.media_type === "image"
                          ? "bg-emerald-50 border-emerald-200 hover:border-emerald-400"
                          : "bg-slate-50 border-slate-200 hover:border-blue-300"
                      } hover:shadow-md`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-slate-800">
                              {index + 1}. {question.text}
                            </span>
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-bold">
                              {question.points || 1} pct
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 text-sm">
                            <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-xs font-medium capitalize">
                              {question.kind}
                            </span>
                            <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-xs">
                              {question.options?.length || 0} opțiuni
                            </span>
                            {question.chapter_index !== null && question.chapter_index !== undefined && selectedQuiz.video_chapters && (
                              <span className="px-2 py-0.5 bg-amber-200 text-amber-700 rounded text-xs font-medium flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                                </svg>
                                {selectedQuiz.video_chapters[question.chapter_index]?.title || `Capitol ${question.chapter_index + 1}`}
                              </span>
                            )}
                            {question.media_type === "video" && (
                              <span className="px-2 py-0.5 bg-purple-200 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                                Video LMS ({question.video_duration}s)
                              </span>
                            )}
                            {question.media_type === "image" && (
                              <span className="px-2 py-0.5 bg-emerald-200 text-emerald-700 rounded text-xs font-medium flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                </svg>
                                Imagine
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditQuestionModal(question)}
                            className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 rounded-lg text-sm transition-all duration-200 font-medium"
                          >
                            Editează
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm transition-all duration-200 font-medium"
                          >
                            Șterge
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Question — shared redesigned modal */}
      {showAddQuestionModal && selectedQuiz && (
        <QuestionEditorModal
          mode="add"
          form={questionForm}
          setForm={setQuestionForm}
          onSubmit={handleAddQuestion}
          onClose={() => { setShowAddQuestionModal(false); resetQuestionForm(); }}
          videoChapters={selectedQuiz?.video_mode === "chapters" ? (selectedQuiz?.video_chapters as any) : null}
        />
      )}

      {showEditQuestionModal && selectedQuestion && (
        <QuestionEditorModal
          mode="edit"
          form={questionForm}
          setForm={setQuestionForm}
          onSubmit={handleUpdateQuestion}
          onClose={() => { setShowEditQuestionModal(false); setSelectedQuestion(null); resetQuestionForm(); }}
          videoChapters={selectedQuiz?.video_mode === "chapters" ? (selectedQuiz?.video_chapters as any) : null}
        />
      )}
    </div>
  );
}
